import { Fragment, FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";

import { TooltipWrapper } from "../../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { translationKeys } from "../../../../models/app-translation-keys";
import { AllLanguageOptions } from "../../../../models/constants/app-language-options.const";
import { ILanguageOption } from "../../../../models/interfaces";
import { useGlossaryService } from "../../../../services/ServiceHooks";
import { useStore } from "../../../../stores/StoreContext";
import { IGlossaryFileSupportExtended } from "./GlossarySelection.types";
import { GlossarySelectionOption } from "./GlossarySelectionOption/GlossarySelectionOption";

interface IGlossarySelectionProps {
  sourceLanguage: ILanguageOption;
  targetLanguage: ILanguageOption;
  onGlossaryChange: (
    glossary: IGlossaryFileSupportExtended | null,
    sourceLanguage: ILanguageOption,
    targetLanguage: ILanguageOption,
  ) => void;
}

export const GlossarySelection: FunctionComponent<IGlossarySelectionProps> = (props: IGlossarySelectionProps) => {
  const { t } = useTranslation();
  const { userStore } = useStore();
  const glossaryService = useGlossaryService();

  const [glossaries, setGlossaries] = useState<IGlossaryFileSupportExtended[]>([]);
  const [filteredGlossaries, setFilteredGlossaries] = useState<IGlossaryFileSupportExtended[]>([]);
  const [selectedGlossary, setSelectedGlossary] = useState<IGlossaryFileSupportExtended | null>(null);

  const glossaryTranslationsKeys = translationKeys.feature.translation.glossary;

  const loadSupportedGlossaries = async () => {
    const response = await glossaryService.getGlossaryFileInfos();

    const glossariesExtended = response?.glossaryFileSupports?.map(glossary => ({
      ...glossary,
      supportedLanguageOptions: glossary.supportedLanguages
        .map(languageCode => AllLanguageOptions.find(langOption => langOption.code.includes(languageCode)))
        .filter((langOption): langOption is ILanguageOption => !!langOption),
    }));

    setGlossaries(glossariesExtended);
  };

  useEffect(() => {
    if (!glossaries.length) {
      loadSupportedGlossaries();
    }
  }, []);

  useEffect(() => {
    const filteredGlossaries = glossaries.filter(
      glossary =>
        glossary.supportedLanguages.length > 1 &&
        glossary.supportedLanguages.some(supportedLanguage => supportedLanguage.includes(props.sourceLanguage.code)) &&
        glossary.supportedLanguages.some(supportedLanguage => supportedLanguage.includes(props.targetLanguage.code)),
    );

    setFilteredGlossaries(filteredGlossaries);
  }, [glossaries, userStore.currentLanguage]);

  const onGlossaryChange = (glossary: IGlossaryFileSupportExtended | null) => {
    setSelectedGlossary(glossary);

    if (!glossary) {
      props.onGlossaryChange(glossary, props.sourceLanguage, props.targetLanguage);
      return;
    }

    const supportedLanguageOptions = glossary.supportedLanguageOptions;

    let sourceLanguage = props.sourceLanguage;
    let targetLanguage = props.targetLanguage;
    if (props.sourceLanguage !== supportedLanguageOptions.find(lang => lang.code.includes(props.sourceLanguage.code))) {
      sourceLanguage = supportedLanguageOptions[0];
    }
    if (props.targetLanguage !== supportedLanguageOptions.find(lang => lang.code.includes(props.targetLanguage.code))) {
      targetLanguage = supportedLanguageOptions[1];
    }

    props.onGlossaryChange(glossary, sourceLanguage, targetLanguage);
  };

  const dropdownItemTemplate = (option: IGlossaryFileSupportExtended) => {
    return <GlossarySelectionOption glossary={option} />;
  };

  return (
    <Fragment>
      <TooltipWrapper
        targetRef=".glossary-selection-dropdown"
        content={t(glossaryTranslationsKeys.selectionTitle)}
        position="left"
      />

      <Dropdown
        value={selectedGlossary}
        options={filteredGlossaries}
        onChange={(e: DropdownChangeEvent) => onGlossaryChange(e.value)}
        optionLabel="name"
        placeholder={t(glossaryTranslationsKeys.placeholder)}
        className="glossary-selection-dropdown"
        filter
        showFilterClear
        filterInputAutoFocus={true}
        filterBy="name"
        tooltipOptions={{ position: "left" }}
        tooltip={t(glossaryTranslationsKeys.selectionTitle)}
        itemTemplate={dropdownItemTemplate}
        panelClassName="dropdown-content-space-between"
        showClear
      />
    </Fragment>
  );
};
